import type { Course, SegmentType } from '@rcs/data-schema';
import type { TrackSections } from '@rcs/renderer';
import type { SimulationOptions } from '@rcs/physics';
import { useEffect, useRef, useState } from 'react';
import { toCourseInput } from './course/input';
import { segmentColors, segmentDecorations } from './course/segment-style';
import { toSegmentSpecs } from './course/segments';
import { createComputeClient, type ComputeClient, type ComputeOutcome } from './worker/client';

/** 編集の直後に、続けて計算し直す間隔 [ms]。ドラッグ中は、最後の位置の計算だけを待たずに更新する。 */
const RECOMPUTE_DELAY_MS = 80;

/** 計算の結果と、計算したコースの ID（コースを切り替えたときに、どのコースの結果か分かるように）。 */
export type RideOutcome = ComputeOutcome & {
  readonly courseId: string;
  /** 計算したときの、制御点ごとの区間種別（3D の枕木の色分けに使う。結果と同じ時点のもの） */
  readonly sections: TrackSections;
  /** 制御点ごとの区間種別（HUD の「いまの区間」の表示に使う。結果と同じ時点のもの） */
  readonly segmentKinds: readonly SegmentType[];
};

/**
 * コースを Web Worker で計算し、結果を返す（初回の計算中は `undefined`）。
 * コースが変わるたびに計算し直す。計算中は、前の結果を表示し続ける。
 * 古い要求の結果は、Worker のクライアントが捨てる（FR-SIM-004）。
 *
 * @param simulation 走行の条件（初速など）。参照が変わると計算し直す
 */
export function useRide(course: Course, simulation?: SimulationOptions): RideOutcome | undefined {
  const [outcome, setOutcome] = useState<RideOutcome | undefined>();
  const clientRef = useRef<ComputeClient | undefined>(undefined);
  const first = useRef(true);

  useEffect(() => {
    const client = createComputeClient();
    clientRef.current = client;
    return () => {
      clientRef.current = undefined;
      client.dispose();
    };
  }, []);

  useEffect(() => {
    const client = clientRef.current;
    if (!client) return undefined;
    const delay = first.current ? 0 : RECOMPUTE_DELAY_MS;
    first.current = false;
    const timer = setTimeout(() => {
      void client
        .compute({
          course: toCourseInput(course),
          simulation: { ...simulation, segments: toSegmentSpecs(course.controlPoints) },
        })
        .then((value) => {
          if (value.status !== 'stale')
            setOutcome({
              ...value,
              courseId: course.id,
              sections: {
                colors: segmentColors(course.controlPoints),
                decorations: segmentDecorations(course.controlPoints),
              },
              segmentKinds: course.controlPoints.map((point) => point.segment),
            });
        });
    }, delay);
    return () => {
      clearTimeout(timer);
    };
  }, [course, simulation]);

  return outcome;
}
